import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const PREVIEW_FADE_MS = 800;
const Ctx = createContext(null);

export const useNowPlaying = () => useContext(Ctx);

/**
 * Global audio engine + Now-Playing state. Wrap the app once and use
 * `useNowPlaying()` anywhere to play/pause + read the current track.
 *
 * Track shape:
 *   { id, title, artist, frequency, duration, url, thumbnail_url, category,
 *     subscription_tier, tier_required, access_level, preview_seconds, category_meta? }
 */
export function NowPlayingProvider({ children }) {
  const navigate = useNavigate();
  const [track, setTrack] = useState(null);   // currently selected track (paused or playing)
  const [isPlaying, setIsPlaying] = useState(false);
  const [previewLeft, setPreviewLeft] = useState(null);
  const [position, setPosition] = useState(0);   // seconds
  const [duration, setDuration] = useState(0);   // seconds
  const audioRef = useRef(null);
  const fadeRef = useRef(null);
  const previewTimerRef = useRef(null);
  const previewIntervalRef = useRef(null);
  const positionIntervalRef = useRef(null);

  const clearTimers = () => {
    [fadeRef, previewTimerRef, previewIntervalRef, positionIntervalRef].forEach((r) => {
      if (r.current) {
        clearInterval(r.current);
        clearTimeout(r.current);
        r.current = null;
      }
    });
  };

  const stopAll = (clearTrack = false) => {
    clearTimers();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.volume = 1;
      audioRef.current = null;
    }
    setIsPlaying(false);
    setPreviewLeft(null);
    setPosition(0);
    setDuration(0);
    if (clearTrack) setTrack(null);
  };

  // Cleanup on unmount
  useEffect(() => () => stopAll(true), []);

  const fadeOutAndStop = () => {
    const a = audioRef.current;
    if (!a) { stopAll(); return; }
    const steps = 16;
    const stepMs = PREVIEW_FADE_MS / steps;
    const init = a.volume;
    let i = 0;
    fadeRef.current = setInterval(() => {
      i += 1;
      if (a) a.volume = Math.max(0, init * (1 - i / steps));
      if (i >= steps) { clearInterval(fadeRef.current); stopAll(); }
    }, stepMs);
  };

  const play = (nextTrack) => {
    if (!nextTrack) return;
    // Toggle pause if it's the same track
    if (track?.id === nextTrack.id && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().catch(() => {});
        setIsPlaying(true);
      }
      return;
    }
    stopAll();
    setTrack(nextTrack);
    const a = new Audio(nextTrack.url);
    a.volume = 1;
    audioRef.current = a;
    a.onloadedmetadata = () => setDuration(a.duration || 0);
    a.onended = () => stopAll(false);
    positionIntervalRef.current = setInterval(() => {
      if (audioRef.current) setPosition(audioRef.current.currentTime || 0);
    }, 500);

    if (nextTrack.access_level === "preview") {
      const sec = Math.max(5, Math.min(60, nextTrack.preview_seconds || 30));
      setPreviewLeft(sec);
      previewIntervalRef.current = setInterval(
        () => setPreviewLeft((p) => p == null ? null : Math.max(0, p - 1)), 1000
      );
      previewTimerRef.current = setTimeout(() => {
        fadeOutAndStop();
        toast.message("Upgrade to Platinum to listen in full", {
          action: { label: "Upgrade", onClick: () => navigate("/subscription") },
        });
      }, sec * 1000);
    }

    a.play()
      .then(() => setIsPlaying(true))
      .catch((err) => {
        // Common in headless / strict-autoplay environments. Keep the track
        // visible in the player UI in paused state so the user can retry by
        // tapping play — only show an error if the URL itself looks broken.
        console.warn("[NowPlaying] play() failed:", err?.name || err);
        setIsPlaying(false);
      });
  };

  const togglePlay = () => {
    if (!track || !audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); setIsPlaying(false); }
    else { audioRef.current.play().catch(() => {}); setIsPlaying(true); }
  };

  const stop = () => stopAll(true);

  return (
    <Ctx.Provider value={{ track, isPlaying, previewLeft, position, duration, play, togglePlay, stop }}>
      {children}
    </Ctx.Provider>
  );
}
